import { createContext, ReactNode, useCallback, useContext, useEffect, useState } from "react";
import { Idea, w2d2dApiClient } from "../../w2d2d-api-client";
import { useWebPush } from "../../../appfw/notifications/use-web-push";
import { useQueryClient } from "@tanstack/react-query";
import { useAppFwContext } from "../../../appfw/AppFwContextContextProvider.tsx";

export interface IdeasControl {
    readonly ideas: Idea[] | undefined;
    readonly updateIdea: (idea: Idea) => void;
    readonly updateIdeaById: (ideaId: string) => Promise<void>;
    readonly removeIdea: (ideaId: string) => void;
    readonly getByEventUri: (eventUri: string) => Idea | undefined;
}

const IdeasControlContext = createContext<IdeasControl | undefined>(undefined);

interface IdeasControlProviderProps {
    children: ReactNode | ReactNode[];
}

export const IdeasControlProvider = (props: IdeasControlProviderProps) => {
    const ideasControl = useIdeasControlImpl();

    return (
        <IdeasControlContext.Provider value={ideasControl}>
            {props.children}
        </IdeasControlContext.Provider>
    );
};

export function useIdeasControl(): IdeasControl {
    const context = useContext(IdeasControlContext);
    if (context === undefined) {
        throw new Error('useIdeasControl must be used within IdeasControlProvider');
    }
    return context;
}

function useIdeasControlImpl(): IdeasControl {
    const [ideas, setIdeas] = useState<Idea[] | undefined>(undefined)
    const queryClient = useQueryClient()
    const appFwContext = useAppFwContext()

    const fetchIdeas = useCallback(async () => {
        try {
            const ideasList = await w2d2dApiClient.getIdeas()
            setIdeas(ideasList.ideas)
        } catch (e) {
            console.error("Failed to fetch ideas", e)
        }
    }, [])

    const updateIdea = useCallback((idea: Idea) => {
        setIdeas((currentIdeas) => {
            if (!currentIdeas) {
                console.log("No ideas list, skipping update")
                return currentIdeas
            }
            console.log("...updating idea: ", idea)
            let modified = false
            let updatedIdeas = currentIdeas.map((i) => {
                if (i.id === idea.id) {
                    modified = true
                    return idea
                }
                return i
            })

            if (!modified) {
                updatedIdeas.push(idea)
                updatedIdeas.sort((a, b) => {
                    const aTs = a.startsAt ? Date.parse(a.startsAt) : 0
                    const bTs = b.startsAt ? Date.parse(b.startsAt) : 0
                    return aTs - bTs
                })
                console.log("Adding new idea to ideas list")
            } else {
                console.log("Updating ideas list")
            }

            return updatedIdeas
        })
    }, [])

    const removeIdea = useCallback((ideaId: string) => {
        setIdeas((currentIdeas) => {
            if (!currentIdeas) {
                console.log("No ideas list, skipping removal")
                return currentIdeas
            }
            console.log("Removing idea: ", ideaId)
            return currentIdeas.filter((i) => i.id !== ideaId)
        })
    }, [])

    const updateIdeaById = useCallback(async (ideaId: string) => {
        console.log("start update idea: ", ideaId)

        try {
            const idea = await queryClient.fetchQuery({
                queryKey: ['ideas', ideaId],
                queryFn: () => w2d2dApiClient.getIdea({ id: ideaId })
            });

            if (idea) {
                updateIdea(idea)
            } else {
                console.log("Idea not found, removing from list: ", ideaId)
                removeIdea(ideaId)
            }
        } catch (err) {
            console.error("Failed to update idea:", err);
        }
    }, [queryClient, updateIdea, removeIdea])

    useWebPush("ideas.participation", async (message: any) => {
        console.log("[pusher] Notification received: ", message)

        const ideaId = message.ideaId
        if (ideaId) {
            await updateIdeaById(ideaId)
        }
    })

    useWebPush("ideas.notify", async (message: any) => {
        console.log("[pusher] Notification received: ", message)

        const ideaId = message.ideaId
        if (ideaId) {
            await updateIdeaById(ideaId)
        }
    })

    useEffect(() => {
        fetchIdeas()
        return appFwContext.registerRefreshCallback(fetchIdeas)
    }, [fetchIdeas, appFwContext])

    const getByEventUri = useCallback((eventUri: string): Idea | undefined => {
        if (!ideas) {
            return undefined
        }

        // Check if the URI is an idea URI pattern: ${baseUrl}ideas/:id
        const ideaUriPattern = new RegExp(`^${w2d2dApiClient.baseUrl.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}ideas/([^/]+)$`);
        const ideaUriMatch = eventUri.match(ideaUriPattern);

        if (ideaUriMatch) {
            // Extract the idea ID from the URI and find by ID
            const ideaId = ideaUriMatch[1];
            return ideas.find(idea => idea.id === ideaId);
        }

        // Otherwise, find by eventUri property
        return ideas.find(idea => idea.eventUri === eventUri);
    }, [ideas]);

    return {
        ideas,
        updateIdea,
        updateIdeaById,
        removeIdea,
        getByEventUri
    }
}

export default IdeasControlProvider
